// Function to generate section data
const getSectionData = (id, section, selector) => ({ id, section, selector });

// Check cart drawer and set sections to render
const cartDrawer = document.querySelector('cart-drawer');
let sectionsToRender = [];

if (cartDrawer) {
  const mainCartId = document.getElementById('main-cart-items')?.dataset.id;
  if (mainCartId) {
    sectionsToRender = [
      getSectionData(`#shopify-section-${mainCartId}`, mainCartId, `#shopify-section-${mainCartId} cart-items`),
      getSectionData('#cart-counter', 'cart-counter', '#shopify-section-cart-counter'),
      getSectionData('#CartDrawer-Body', 'cart-drawer', '#shopify-section-cart-drawer #CartDrawer-Body')
    ];
  } else {
    sectionsToRender = [getSectionData('#CartDrawer-Body', 'cart-drawer', '#shopify-section-cart-drawer #CartDrawer-Body')];
  }
} else {
  const mainCartId = document.getElementById('main-cart-items')?.dataset.id;
  if (mainCartId) {
    sectionsToRender = [
      getSectionData(`#shopify-section-${mainCartId}`, mainCartId, `#shopify-section-${mainCartId} cart-items`),
      getSectionData('#cart-counter', 'cart-counter', '#shopify-section-cart-counter')
    ];
  } else {
    sectionsToRender = [];
  }
}

// Global serialized cart-mutation coordinator shared by every cart surface
// (main cart and drawer): at most one /cart/change request may be in flight,
// so the server applies mutations in intent order and the older request can
// never become authoritative. While a request is active only the newest
// desired intent is retained; when the active request settles, that intent is
// issued against identity refreshed from the authoritative response. Only the
// newest intent may render sections, counters, focus, or errors.
const cartMutationCoordinator = { ticket: 0, active: null, pending: null };

// Customily groups add-on lines under their parent only inside the cart element
// it found, and re-rendering the cart replaces that element. This is the event
// Customily fires itself after its own add to cart to find the new one.
const refreshCustomilyCart = () => window.dispatchEvent(new CustomEvent('customily-trigger-cart-handler'));

const escapeAttributeValue = value =>
  window.CSS && typeof window.CSS.escape === 'function' ? window.CSS.escape(String(value)) : String(value).replace(/"/g, '\\"');

// Loading ownership is per element: only the element's newest intent may
// clear its own `is-loading`, so a superseded intent settles its spinner
// without stealing it from a newer request on the same surface.
const retireCartIntentLoading = intent => {
  if (intent.element.loadingIntent === intent.id) intent.element.classList.remove('is-loading');
};

const CART_MUTATION_RECOVERY_MESSAGE = 'Cart update status is unclear. Refresh your cart before trying again.';
const CART_PRESENTATION_RECOVERY_MESSAGE = 'Your cart was updated, but the cart display could not refresh. Refresh your cart to continue.';

function cartPresentationRecoveryError(cause) {
  const error = new Error(CART_PRESENTATION_RECOVERY_MESSAGE);
  error.cartMutationConfirmed = true;
  error.cause = cause;
  return error;
}

function renderCartMutationRecovery(intent, error) {
  if (error) console.error(error);
  const recoveryMessage = error?.cartMutationConfirmed ? CART_PRESENTATION_RECOVERY_MESSAGE : CART_MUTATION_RECOVERY_MESSAGE;
  try {
    const selector = intent.key
      ? `[data-line-item-error][data-key="${escapeAttributeValue(intent.key)}"]`
      : `[data-line-item-error][data-line="${intent.line}"]`;
    const matchingRegions = Array.from(document.querySelectorAll(selector));
    if (matchingRegions.length) {
      intent.element.updateLiveRegions(
        intent.line,
        intent.key,
        intent.element.currentItemCount,
        recoveryMessage
      );
      return;
    }
    let recovery = intent.element.querySelector?.('[data-cart-mutation-recovery]');
    if (!recovery && document.createElement) {
      recovery = document.createElement('p');
      recovery.className = 'cart-item__error color-red';
      recovery.setAttribute('role', 'alert');
      recovery.setAttribute('data-cart-mutation-recovery', '');
      intent.element.prepend?.(recovery);
    }
    if (recovery) {
      recovery.textContent = recoveryMessage;
      recovery.style.display = 'block';
    }
  } catch (recoveryError) {
    console.error(recoveryError);
  }
}

function issuePlayersPackMutation(intent) {
  cartMutationCoordinator.active = intent;
  const api = window.GSPlayersPack;
  if (!api?.mutateGroup || !api?.readSections) {
    const error = new Error('Players Pack cart controls are unavailable.');
    api?.markCartInvalid?.(error.message);
    settleCartMutation(intent, null, error);
    return;
  }
  Promise.resolve()
    .then(async () => {
      const sections = intent.element.getSectionsToRender().map(section => section.section);
      const result = await api.mutateGroup({
        fetchImpl: window.fetch.bind(window),
        groupId: intent.playersPackGroupId,
        targetQuantity: Number.parseInt(intent.quantity, 10),
        sections,
        sectionsUrl: window.location.pathname
      });
      try {
        let sectionBodies;
        try {
          sectionBodies = await api.readSections(window.fetch.bind(window), sections, window.location.pathname);
        } catch (_) {
          // The mutation is already proven by mutateGroup's authoritative
          // post-write /cart.js read. Retry only the idempotent section read;
          // never replay the landed cart mutation.
          sectionBodies = await api.readSections(window.fetch.bind(window), sections, window.location.pathname);
        }
        return { parsedState: { ...result.cart, sections: sectionBodies }, error: null };
      } catch (sectionError) {
        const error = cartPresentationRecoveryError(sectionError);
        return { parsedState: result.cart, error };
      }
    })
    .then(
      ({ parsedState, error }) => {
        if (error) api.markCartInvalid(error.message);
        settleCartMutation(intent, parsedState, error);
      },
      error => {
        api.markCartInvalid(error instanceof Error ? error.message : api.BLOCKED_MESSAGE);
        settleCartMutation(intent, null, error);
      }
    );
}

// Customily bundles (e.g. the Gift Box): a parent line (_customily-upsell-main-id)
// with $0 add-on lines (_customily-upsell-id) changes as one group, like the
// Players Pack. The parent and every add-on (times its multiplier) are set in one
// /cart/update.js, read from the live cart, so the contents always match the
// boxes. ?customily_upsell keeps Customily's own cart interceptor out of it.
// line-item.liquid marks such parents with data-gs-addon-main-id.
function customilyBundleMainId(key) {
  const row = document.querySelector(`.cart-item[data-key="${escapeAttributeValue(key)}"][data-gs-addon-main-id]`);
  return row?.dataset.gsAddonMainId || '';
}

async function customilyBundleUpdates(intent, mainId) {
  const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Cart could not be read before the update.');
  const cart = await response.json();
  const parent = cart.items?.find(item => item.key === intent.key);
  if (!parent || parent.properties?.['_customily-upsell-main-id'] !== mainId) return null;
  const quantity = Number.parseInt(intent.quantity, 10);
  if (!Number.isInteger(quantity) || quantity < 0) return null;
  const updates = { [parent.key]: quantity };
  cart.items.forEach(item => {
    const properties = item.properties || {};
    if (properties['_customily-upsell-id'] !== mainId || item.final_line_price !== 0 || properties._gs_players_pack_contract) return;
    updates[item.key] = quantity * (Number(properties['_customily-upsell-multiplier']) || 1);
  });
  return Object.keys(updates).length > 1 ? updates : null;
}

async function readCartMutationResponse(response) {
  let parsedState;
  try {
    parsedState = JSON.parse(await response.text());
  } catch (_) {
    throw new Error('Cart update returned an invalid response.');
  }
  if (!response.ok || parsedState?.errors) {
    throw new Error(parsedState?.description || parsedState?.message || 'Cart update request failed.');
  }
  if (!parsedState || !Array.isArray(parsedState.items) || !parsedState.sections || typeof parsedState.sections !== 'object') {
    throw new Error('Cart update returned an incomplete response.');
  }
  return parsedState;
}

function issueCartMutation(intent) {
  if (intent.playersPackGroupId) {
    issuePlayersPackMutation(intent);
    return;
  }
  // A stable Shopify line-item key is mandatory mutation authority. Rendered
  // positions are retained only for DOM/live-region targeting and are never
  // sent to Shopify.
  if (!intent.key) {
    renderCartMutationRecovery(intent, new Error('Cart line identity is unavailable.'));
    retireCartIntentLoading(intent);
    return;
  }
  cartMutationCoordinator.active = intent;
  const sections = intent.element.getSectionsToRender().map(section => section.section);
  const sectionsUrl = window.location.pathname;
  const changeLine = () => fetch(`${routes.cart_change_url}`, {
    ...fetchConfig(),
    ...{ body: JSON.stringify({ id: intent.key, quantity: intent.quantity, sections, sections_url: sectionsUrl }) }
  });
  const mainId = customilyBundleMainId(intent.key);
  const request = mainId
    ? customilyBundleUpdates(intent, mainId).then(updates => (updates
      ? fetch(`${routes.cart_update_url}.js?customily_upsell`, {
        ...fetchConfig(),
        ...{ body: JSON.stringify({ updates, sections, sections_url: sectionsUrl }) }
      })
      : changeLine()))
    : changeLine();
  request
    .then(readCartMutationResponse)
    .then(
      parsedState => settleCartMutation(intent, parsedState, null),
      error => settleCartMutation(intent, null, error)
    );
}

function rebindCartIntentIdentity(pending, parsedState) {
  // A queued intent may only mutate a line it can still positively identify in
  // the authoritative response. Positional lines cannot be re-verified after
  // an intervening mutation, so they are never re-issued from the queue.
  const items = Array.isArray(parsedState.items) ? parsedState.items : [];
  if (pending.key && items.some(item => String(item.key || '') === pending.key)) return pending;
  if (pending.playersPackGroupId) {
    const api = window.GSPlayersPack;
    if (!api?.isMarkedLine) return null;
    const matches = items.filter(item => {
      const properties = item.properties || {};
      const groupId = properties._bundle_group_id ?? properties.bundle_group_id;
      const role = properties._bundle_role ?? properties.bundle_role;
      return api.isMarkedLine(item) && String(groupId || '') === pending.playersPackGroupId && role === 'parent' && item.key;
    });
    return matches.length === 1
      ? { ...pending, key: String(matches[0].key), variantId: String(matches[0].variant_id || matches[0].variantId || '') }
      : null;
  }
  if (!pending.key) return null;
  if (!pending.variantId) return null;
  const matches = items.filter(item => String(item.variant_id || item.variantId || '') === pending.variantId && item.key);
  // A line-key rotation is safe to follow only when the authoritative cart
  // contains exactly one line for the intended variant. Multiple personalized
  // lines with the same variant are deliberately ambiguous and fail closed.
  if (matches.length === 1) return { ...pending, key: String(matches[0].key) };
  return null;
}

function settleCartMutation(intent, parsedState, error) {
  cartMutationCoordinator.active = null;
  const pending = cartMutationCoordinator.pending;
  cartMutationCoordinator.pending = null;
  let pendingIssued = false;
  try {
    if (!pending) {
      if (error) renderCartMutationRecovery(intent, error);
      else intent.element.renderCartMutation(intent, parsedState);
      return;
    }
    // A superseded response must not paint sections, counters, focus, or
    // errors. Its authoritative state is used only to rebind the newest intent.
    if (!parsedState) {
      renderCartMutationRecovery(pending, error || new Error('Cart update status is ambiguous.'));
      return;
    }
    const rebound = rebindCartIntentIdentity(pending, parsedState);
    if (rebound) {
      pendingIssued = true;
      issueCartMutation(rebound);
      return;
    }
    if (error) {
      // The active mutation landed, but the drawer could not refresh and the
      // queued target no longer exists. Do not mislabel known cart truth as an
      // ambiguous mutation or attempt a duplicate write.
      renderCartMutationRecovery(pending, error);
      return;
    }
    // Fail closed: the target disappeared or cannot be uniquely rebound.
    // Render the authoritative settled cart, then explicitly tell the shopper
    // that their newer requested quantity was not applied.
    pending.element.renderCartMutation(pending, parsedState, { failClosed: true });
    renderCartMutationRecovery(pending);
  } catch (settleError) {
    const recoveryError = parsedState && (intent.playersPackGroupId || pending?.playersPackGroupId)
      ? cartPresentationRecoveryError(settleError)
      : settleError;
    if (intent.playersPackGroupId || pending?.playersPackGroupId) {
      window.GSPlayersPack?.markCartInvalid?.(
        recoveryError instanceof Error ? recoveryError.message : window.GSPlayersPack.BLOCKED_MESSAGE
      );
    }
    renderCartMutationRecovery(pending || intent, recoveryError);
  } finally {
    retireCartIntentLoading(intent);
    if (pending && !pendingIssued) retireCartIntentLoading(pending);
  }
}

function handleCartInputNames() {
  const isMobile = DeviceDetector.isMobile();

  const activate = input => {
    input.setAttribute('name', 'updates[]');
    input.removeAttribute('data-name');
    input.disabled = false;
  };

  const deactivate = input => {
    input.setAttribute('data-name', 'updates[]');
    input.removeAttribute('name');
    input.disabled = true;
  };

  document.querySelectorAll('.cart-item').forEach(item => {
    const mobileInput = item.querySelector('.js-qty-input-mobile input.quantity__input');
    const desktopInput = item.querySelector('.js-qty-input-desktop input.quantity__input');

    if (!mobileInput || !desktopInput) return;

    const activeInput = isMobile ? mobileInput : desktopInput;
    const inactiveInput = isMobile ? desktopInput : mobileInput;
    activate(activeInput);
    deactivate(inactiveInput);
    const playersPackGroupId = item.dataset.playersPackGroupId;
    if (playersPackGroupId) activeInput.dataset.playersPackGroupId = playersPackGroupId;
    else activeInput.removeAttribute('data-players-pack-group-id');
    inactiveInput.removeAttribute('data-players-pack-group-id');
  });
}

window.addEventListener('resize', handleCartInputNames);
document.addEventListener('DOMContentLoaded', handleCartInputNames);
document.addEventListener('shopify:section:load', handleCartInputNames);

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', event => {
      event.preventDefault();
      const cartItems = this.closest('cart-drawer-items') || this.closest('cart-items');
      cartItems.updateQuantity(
        this.dataset.index,
        0,
        undefined,
        this.dataset.key,
        this.dataset.variantId,
        this.dataset.playersPackGroupId
      );
      updateFreeShipping();
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.freeShipping = document.querySelectorAll('shipping-bar');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0
    );

    this.debouncedOnChange = debounce(event => {
      this.onChange(event);
    }, 300);
    this.addEventListener('change', this.debouncedOnChange.bind(this));

    updateFreeShipping();
  }

  calculateTotalItemCount(items) {
    return window.GSPlayersPack?.shopperItemCount(items)
      ?? items.reduce((total, item) => total + item.quantity, 0);
  }

  onChange(event) {
    if (event.target.name !== 'updates[]') return;

    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.key,
      event.target.dataset.variantId,
      event.target.dataset.playersPackGroupId
    );
  }

  getSectionsToRender() {
    return sectionsToRender;
  }

  updateQuantity(line, quantity, name, key, variantId, playersPackGroupId) {
    this.classList.add('is-loading');

    const intent = {
      id: ++cartMutationCoordinator.ticket,
      element: this,
      line,
      quantity,
      name,
      key: key || '',
      variantId: variantId ? String(variantId) : '',
      playersPackGroupId: playersPackGroupId ? String(playersPackGroupId) : ''
    };
    this.loadingIntent = intent.id;
    // A keyless intent is rejected before it can enter the global queue. Its
    // current positional line is safe only for immediate DOM recovery, never
    // for mutation or post-rerender recovery.
    if (!intent.key && !intent.playersPackGroupId) {
      renderCartMutationRecovery(intent, new Error('Cart line identity is unavailable.'));
      retireCartIntentLoading(intent);
      return;
    }
    if (cartMutationCoordinator.active) {
      // Coalesce: while a request is active only the newest desired intent
      // survives; it is issued when the active request settles.
      const replaced = cartMutationCoordinator.pending;
      cartMutationCoordinator.pending = intent;
      // A replaced queued intent will never settle; release its surface's
      // spinner unless a newer intent on that surface owns it.
      if (replaced) retireCartIntentLoading(replaced);
      return;
    }
    issueCartMutation(intent);
  }

  renderCartMutation(intent, parsedState, { failClosed = false } = {}) {
    this.querySelector?.('[data-cart-mutation-recovery]')?.remove?.();
    // Prepare every replacement before mutating the DOM. A missing body,
    // selector, or malformed section therefore fails closed with zero partial
    // paint; settleCartMutation will surface recovery and retire loading.
    const replacements = this.getSectionsToRender()?.map(section => {
      const elementToReplace = document.querySelector(section.selector) || document.querySelector(section.id);
      if (!elementToReplace) throw new Error(`Element with selector ${section.selector} not found`);
      const sectionBody = parsedState.sections[section.section];
      if (typeof sectionBody !== 'string') throw new Error(`Cart response is missing section ${section.section}`);
      return {
        element: elementToReplace,
        html: this.getSectionInnerHTML(sectionBody, section.selector)
      };
    }) || [];
    replacements.forEach(replacement => { replacement.element.innerHTML = replacement.html; });
    refreshCustomilyCart();
    if (!parsedState.errors) {
      this.totalItemCount = this.calculateTotalItemCount(parsedState.items);
    }
    // A fail-closed render restores the authoritative cart without painting
    // the superseded response's error or focus as if the dropped intent ran.
    this.updateLiveRegions(intent.line, intent.key, parsedState.item_count, failClosed ? null : parsedState.errors);

    if (!failClosed) {
      const lineItem =
        (intent.key && document.querySelector(`.cart-item[data-key="${escapeAttributeValue(intent.key)}"]`)) ||
        document.getElementById(`CartItem-${intent.line}`);
      if (lineItem && lineItem.querySelector(`[name="${intent.name}"]`)) lineItem.querySelector(`[name="${intent.name}"]`).focus();
    }

    handleCartInputNames();
    window.GSPlayersPack?.applyCartPresentation?.(parsedState);
    updateCartCounters();
    updateFreeShipping();
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  updateLiveRegions(line, key, itemCount, parsedError) {
    if (parsedError) {
      let errorRegions = key
        ? Array.from(document.querySelectorAll(`[data-line-item-error][data-key="${escapeAttributeValue(key)}"]`))
        : [];
      if (!errorRegions.length && !key) {
        errorRegions = Array.from(document.querySelectorAll(`[data-line-item-error][data-line="${line}"]`));
      }
      errorRegions.forEach(error => {
        error.innerHTML = parsedError;
        error.style.display = 'block';
      });
    }

    this.currentItemCount = itemCount;
  }
}
customElements.define('cart-items', CartItems);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', event => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.setCartLink();
    this.parentElement.addEventListener('shopify:section:select', () => this.open());
    this.parentElement.addEventListener('shopify:section:deselect', () => this.close());
  }

  setCartLink() {
    const cartLink = document.querySelector('[data-cart-link]');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', event => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', event => {
        if (event.code.toUpperCase() !== 'SPACE') return;
        event.preventDefault();
        this.open(cartLink);
      });
    } else {
      console.error('Cart link not found');
    }
  }

  open(opener) {
    if (opener) this.setActiveElement(opener);
    this.classList.add('is-visible');
    document.querySelector('body').style.overflow = 'hidden';
    this.addEventListener(
      'transitionend',
      () => {
        this.focusOnCartDrawer();
      },
      { once: true }
    );

    setTimeout(() => {
      document.addEventListener('click', this.handleOutsideClick);
    }, 100);

    const productReccomendations = document.querySelector('.product-recommendations');
    if (productReccomendations) {
      if (productReccomendations.classList.contains('hidden')) {
        document.querySelector('.cart-drawer-items').classList.add('cart-drawer-items__full');
      } else {
        document.querySelector('.cart-drawer-items').classList.remove('cart-drawer-items__full');
      }
    }
  }

  close() {
    this.classList.remove('is-visible');
    document.querySelector('body').style.overflow = 'auto';
    removeTrapFocus(this.activeElement);

    document.removeEventListener('click', this.handleOutsideClick);

    const isHeaderMenuOpen = header.classList.contains('menu-open');

    if (isHeaderMenuOpen) {
      return;
    }

    // if we are on the cart page, resubmit form
    if (window.location.pathname === '/cart') {
      const cartDrawerForm = document.getElementById('CartDrawer-FormSummary');
      if (cartDrawerForm) {
        cartDrawerForm.submit();
      }
    }
  }

  handleOutsideClick = event => {
    const cartDrawerInner = this.querySelector('.cart-drawer__inner');
    if (cartDrawerInner && !cartDrawerInner.contains(event.target)) {
      this.close();
    }
  };

  setActiveElement(element) {
    this.activeElement = element;
  }

  focusOnCartDrawer() {
    const containerToTrapFocusOn = this.firstElementChild;
    const focusElement = this.querySelector('[data-drawer-close]');
    trapFocus(containerToTrapFocusOn, focusElement);
  }

  renderContents(response, open = true) {
    this.getSectionsToRender()?.forEach(section => {
      const sectionElement = document.querySelector(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(response.sections[section.section], section.selector);

      updateCartCounters();
    });
    refreshCustomilyCart();
    if (response?._gs_players_pack_authoritative_cart === true) {
      window.GSPlayersPack?.applyCartPresentation?.(response);
    }
    handleCartInputNames();
    if (!open) {
      return;
    }

    this.open();
  }

  getSectionsToRender() {
    return sectionsToRender;
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}
customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return sectionsToRender;
  }
}
customElements.define('cart-drawer-items', CartDrawerItems);
